class IaVideo extends HTMLElement {
  // Static constants
  static BREAKPOINT = 1024;
  static OBSERVER_THRESHOLD = 0.5;
  
  constructor() {
    super();
    // Bind methods to preserve context
    this.handleResize = this.handleResize.bind(this);
    this.togglePlay = this.togglePlay.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.backgroundMode = this.dataset.backgroundMode === "true";
  }

  connectedCallback() {
    // Initialize elements
    this.playBtn = this.querySelector("[data-play-btn]");
    this.desktopVideo = this.querySelector(".js-desktop-video") || this.querySelector("video");
    this.mobileVideo = this.querySelector(".js-mobile-video") || this.querySelector("video");
    this.activeVideo = null;
    
    // Set up the component
    this.updateActiveVideo();
    this.setupEventListeners();
    
    // Set up autoplay if needed
    if (this.shouldAutoplay()) {
      window.addEventListener("resize", this.handleResize);
      this.setupIntersectionObserver();
    }
  }
  
  disconnectedCallback() {
    this.cleanupObserver();
    window.removeEventListener("resize", this.handleResize);
  }
  
  /*
   * Public API Methods
   */
  play() {
    if (this.activeVideo) {
      this.activeVideo.play().catch(e => {
        console.warn("Video playback was prevented:", e);
      });
    }
  }
  
  pause() {
    if (this.backgroundMode) { return; }
    if (this.activeVideo) {
      this.activeVideo.pause();
    }
  }
  
  togglePlay() {
    if (!this.activeVideo) return;
    
    if (this.activeVideo.paused) {
      this.play();
    } else {
      this.pause();
    }
  }
  
  /*
   * Private Implementation Methods
   */
  shouldAutoplay() {
    return this.dataset.autoplay === "true" || 
           (this.desktopVideo && this.desktopVideo.autoplay) || 
           (this.mobileVideo && this.mobileVideo.autoplay);
  }
  
  setupEventListeners() {
    // Play button click handler
    if (this.playBtn) {
      this.playBtn.addEventListener("click", this.togglePlay);
    }
    
    // Set up video event listeners
    Array.from(new Set([this.desktopVideo, this.mobileVideo]))
      .filter(Boolean)
      .forEach(video => {
        // Click to play/pause
        video.addEventListener("click", () => {
          console.log(">>> video clicked", video);
          if (video === this.activeVideo) {
            console.log(">>> video active");
            this.togglePlay();
          }
        });
        
        // Update UI on video events
        video.addEventListener("ended", () => {
          if (video === this.activeVideo && this.playBtn) {
            this.playBtn.classList.add("is-hidden");
          }
        });
        
        video.addEventListener("pause", () => {
          this.cleanupObserver();
          if (video === this.activeVideo) {
            this.updateUIForPausedState();
          }
        });
        
        video.addEventListener("play", () => {
          if (video === this.activeVideo) {
            this.updateUIForPlayingState();
            this.pauseOtherVideos();
          }
        });
      });
  }
  
  updateUIForPausedState() {
    if (this.playBtn) {
      this.playBtn.classList.remove("is-hidden");
    }
    this.classList.remove("is-playing");
  }
  
  updateUIForPlayingState() {
    if (this.playBtn) {
      this.playBtn.classList.add("is-hidden");
    }
    this.classList.add("is-playing");
  }
  
  handleResize() {
    this.updateActiveVideo();
    this.cleanupObserver();
    this.setupIntersectionObserver();
  }
  
  updateActiveVideo() {
    const previousVideo = this.activeVideo;
    
    // Select the appropriate video based on viewport width
    if (window.innerWidth >= IaVideo.BREAKPOINT && this.desktopVideo) {
      this.activeVideo = this.desktopVideo;
      if (this.mobileVideo) this.mobileVideo.pause();
    } else if (window.innerWidth < IaVideo.BREAKPOINT && this.mobileVideo) {
      this.activeVideo = this.mobileVideo;  
      if (this.desktopVideo) this.desktopVideo.pause();
    } else {
      this.activeVideo = this.desktopVideo;
    }
    
    // Update UI state if the active video changed
    if (previousVideo !== this.activeVideo && this.activeVideo) {
      if (this.activeVideo.paused) {
        this.updateUIForPausedState();
      } else {
        this.updateUIForPlayingState();
      }
    }
  }
  
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      this.handleIntersection,
      { threshold: IaVideo.OBSERVER_THRESHOLD }
    );
    
    this.observer.observe(this);
  }
  
  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.playForViewport();
      }
    });
  }
  
  cleanupObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
  
  pauseOtherVideos() {
    document.querySelectorAll("video").forEach(video => {
      if (video !== this.activeVideo) {
        video.pause();
      }
    });
  }
  
  playForViewport() {
    this.updateActiveVideo();
    
    if (this.activeVideo && this.dataset.autoplay === "true") {
      this.play();
    }
  }
}

customElements.define("ia-video", IaVideo);